/* eslint-disable import/extensions */
import { PAGES } from '../../constants/GLOBAL_VARIABLE.js'
import {
  STATUS, ERROR_CODE, ERROR_NAME, SERVER_ERROR_NAME,
} from './enum.js'
import { Feature } from '../common'

class OAuth extends Feature {
  constructor({ mainUrl, corsList }) {
    super({ namespace: 'iac', enumKey: 'IAC.OAUTH' })
    this.mainUrl = mainUrl || 'https://signin.nexon.com'

    const {
      client_id: clientId,
      response_type: responseType,
      // redirect_uri: redirectUri,
      scope,
      state,
      error_code: errorCode,
      error_name: errorName,
    } = this.queryParamWithCookie

    const redirectUri = this.getRedirectUriByQueryParamWithCookie({ corsList })

    this.clientId = clientId
    this.responseType = responseType
    this.scope = scope
    this.state = state
    this.redirectUri = redirectUri
    this.errorCode = errorCode
    this.errorName = errorName
  }

  checkRequiredParam() {
    // NOTE : 기존 로직 영향성이 없도록 하기위해 신규 메소드에만 적용, 검증 후 기존 메소드에도 적용 검토
    if (!this.clientId || !this.responseType || !this.redirectUri || !this.scope) {
      console.error(
        'Please check your request query params.',
        `\nclient_id: ${this.clientId}\nresponse_type: ${this.responseType}\nredirect_uri: ${this.redirectUri}\nscope: ${this.scope}`,
      )
      return { status: STATUS.FAIL, errorCode: ERROR_CODE.INVALID_REQUEST }
    }
    return null
  }

  /**
   * Authorization Code 발급 요청
   * @return {object} { code: number, data: object, response: Response, errorData: object }
   */
  async getAuthorizationCode() {
    try {
      if (!this.clientId || !this.responseType || !this.redirectUri || !this.scope) {
        console.error(
          'Please check your request query params.',
          `\nclient_id: ${this.clientId}\nresponse_type: ${this.responseType}\nredirect_uri: ${this.redirectUri}\nscope: ${this.scope}`,
        )
        return { status: STATUS.FAIL, errorCode: ERROR_CODE.INVALID_REQUEST }
      }

      const params = new URLSearchParams({
        client_id: this.clientId,
        response_type: this.responseType,
        redirect_uri: this.redirectUri,
        scope: this.scope || '',
        lang: window.$i18next.language,
      })

      const url = `${this.mainUrl}/iac/v4/oauth/authorize?${params.toString()}`

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include', // 쿠키 전송(webToken)
      })

      if (!response.ok) {
        throw response
      }

      const data = await response.json()
      if (data.code) {
        return { status: STATUS.OK, code: data.code }
      }

      return { status: STATUS.FAIL, errorCode: ERROR_CODE.INVALID_RESPONSE }
    } catch (e) {
      const errorData = typeof e.json === 'function' ? await e.json() : e
      console.error(e)

      if (e.status === 403 && errorData?.error?.name === SERVER_ERROR_NAME.NOT_FOUND_USER_CONSENT) {
        // 동의/허가 필요
        const property = errorData?.error?.property || {}
        return { status: STATUS.CONSENT, property }
      }

      if (errorData?.error?.name) {
        return {
          status: STATUS.FAIL,
          errorCode: ERROR_CODE.FAIL_TO_GET_AUTHORIZATION_CODE,
          errorName: errorData?.error?.name,
        } // iac_error
      }

      return { status: STATUS.FAIL, errorCode: ERROR_CODE.FAIL_TO_GET_AUTHORIZATION_CODE } // server_error
    }
  }

  // OAuth 접근시 등록된 Client인지 검증
  async checkVerifyClient(platformType) {
    try {
      this.checkRequiredParam()

      const url = `${this.mainUrl}/iac/v1/oauth/verify/client`
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // 쿠키 전송(webToken)
        mode: 'cors',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: this.clientId,
          redirect_uri: this.redirectUri,
          platform_type: platformType.toUpperCase(),
        }),
      })

      const data = await response.json()
      if (!response.ok) {
        this.redirectForFail(ERROR_CODE.IAC_INVALID_PLATFORM_TYPE, data?.error?.name)
      }

      if (data?.verified_client) {
        return { status: STATUS.OK, accessCode: data?.krpc_member_access_code }
      }
      this.redirectForFail(ERROR_CODE.IAC_INVALID_PLATFORM_TYPE, data?.error?.name)
      return { status: STATUS.FAIL, errorCode: ERROR_CODE.INVALID_RESPONSE }
    } catch (e) {
      console.error(e)
      this.redirectForFail(ERROR_CODE.IAC_UNKNOWN_ERROR, ERROR_NAME['113307'])
      return { status: STATUS.FAIL, errorCode: ERROR_CODE.INVALID_RESPONSE }
    }
  }

  hasError() {
    // 유저 동의 페이지에서 넘어온 에러가 있는지
    if (this.errorCode || this.errorName) {
      this.redirectForFail(this.errorCode, this.errorName)
    }

    return !!(this.errorCode || this.errorName)
  }

  async processAuthorization() {
    // authorization code 발급 요청
    const result = await this.getAuthorizationCode()

    switch (result.status) {
      case STATUS.OK:
        this.redirectForSuccess(result.code)
        break
      case STATUS.CONSENT:
        const cookieData = {
          client_id: this.clientId,
          response_type: this.responseType,
          scope: this.scope,
          state: this.state,
          redirect_uri: this.redirectUri ? encodeURIComponent(this.redirectUri) : '',
          // NOTE: 250529 property 속성에 할당된 값이 커질 경우, 쿠키 제한 사이즈를 초과하여 비정상 동작하는 이슈가 있어 주석 처리합니다.
          // ...result.property,
          signout_redirect_uri: encodeURIComponent(window.location.href),
        }
        this.namespaceCookie.setCookie(cookieData)
        this.gotoConsentPage()
        break
      case STATUS.FAIL:
        this.redirectForFail(result.errorCode, result.errorName)
        break
      default:
    }
  }

  /**
   * goto 정보제공동의 페이지
   */
  gotoConsentPage() {
    this.navigateToUrlWithoutHistory(`${this.mainUrl}/${PAGES.IAC_CONSENT_OAUTH}`)
  }

  redirectForSuccess(code) {
    const params = new URLSearchParams({ code })
    this.redirectWithParams(params)
  }

  redirectForFail(errorCode, errorName) {
    const params = new URLSearchParams({
      error_code: errorCode,
      error_name: errorName || ERROR_NAME[errorCode],
    })

    this.redirectWithParams(params)
  }

  redirectWithParams(params) {
    if (this.state) {
      params.append('state', this.state)
    }

    super.redirectWithParams(this.redirectUri, params)
  }
}

export default OAuth
